/**
 * QRVerifyPage - QR 코드 검증 및 테이블 세션 시작 페이지
 *
 * QR 스캔 후 /qr/:qrCode 경로로 이동하면 이 페이지가 렌더링됩니다.
 * - pickup QR: 자동으로 해당 매장 페이지로 리다이렉트
 * - table QR: 인원 수 선택 후 세션 시작
 *
 * @see Issue #208 - QR 스캔 기능 구현
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Users, ArrowLeft, Clock } from 'lucide-react';
import { verifyQrCode, startTableSession, extractOccupiedTableSession } from '@/lib/api/qr';
import type { QrVerifyResponse, QrTableVerifyResponse, QrActiveSession } from '@/lib/api/qr';
import { ApiClientError } from '@/lib/api/client';
import { getRestaurantPath, getRestaurantTablePath } from '@/config/routes';
import { setLocationAnchor, clearLocationAnchor } from '@/lib/locationAnchor';
import {
  persistTableSession,
  persistTableSessionEntry,
  restoreActiveTableSessionFromStorage,
} from '@/lib/tableContext';
import { logger } from '@/utils/logger';
import { useApp } from '@/contexts/AppContext';
import { getQrSessionTranslation } from '@/i18n/qrSession';
import { getQrOccupiedTranslation } from '@/i18n/qrOccupied';
import { requestCurrentCoordinates } from '@/lib/geolocation';

type VerifyState =
  | { status: 'loading' }
  | { status: 'table'; data: QrTableVerifyResponse }
  | { status: 'starting-session'; data: QrTableVerifyResponse }
  /** #674 — 점유된 테이블 (다른 일행의 활성 세션 존재, 본 기기에 복원 토큰 없음) */
  | { status: 'occupied'; session: QrActiveSession; data: QrTableVerifyResponse | null }
  | { status: 'disabled'; message: string }
  | { status: 'error'; message: string; data?: QrTableVerifyResponse };

function getDisabledQrMessage(locale: string): string {
  if (locale.startsWith('es')) {
    return 'Este código QR está desactivado. Solicita ayuda al personal.';
  }

  if (locale.startsWith('en')) {
    return 'This QR code is disabled. Please ask the staff for help.';
  }

  return '이 QR 코드는 비활성화되어 있습니다. 직원에게 문의해 주세요.';
}

function getProximityErrorMessage(
  code: string | null | undefined,
  translation: ReturnType<typeof getQrSessionTranslation>,
): string | null {
  if (code === 'TOO_FAR') {
    return translation.gpsTooFar
      ?? 'You are too far from the restaurant. Move closer and try again.';
  }

  if (code === 'GPS_UNAVAILABLE') {
    return translation.gpsUnavailable
      ?? 'We could not verify your location. Ask staff to open the session at the POS.';
  }

  return null;
}

/**
 * 점유 세션 경과 시간(분) 산출 (#674).
 * 서버 duration_minutes 우선, 없으면 started_at 기준 클라이언트 계산.
 * 둘 다 불가하면 null (요약 행 미표시 — fail-safe).
 */
function resolveElapsedMinutes(session: QrActiveSession): number | null {
  if (
    typeof session.duration_minutes === 'number' &&
    Number.isFinite(session.duration_minutes) &&
    session.duration_minutes >= 0
  ) {
    return Math.floor(session.duration_minutes);
  }

  const startedAt = Date.parse(session.started_at);
  if (!Number.isFinite(startedAt)) return null;

  return Math.max(0, Math.floor((Date.now() - startedAt) / 60000));
}

/**
 * started_at ISO 문자열에서 HH:mm 을 직접 추출 (#674 codex P2 — 매장 현지 시각).
 *
 * BE 는 매장 타임존(app.timezone) 오프셋이 박힌 ISO 8601 을 보내므로
 * (예: "2026-06-10T18:30:00-06:00"), 문자열의 시각이 곧 매장 현지 시각이다.
 * toLocaleTimeString(브라우저 타임존) 변환은 여행자/VPN 환경에서 오표시되므로
 * 문자열 그대로 파싱. 형식 불일치 시 null (요약 행 미표시 — fail-safe).
 */
function formatStartedAt(startedAt: string): string | null {
  const match = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(startedAt);
  if (!match) return null;
  return `${match[1]}:${match[2]}`;
}

/**
 * QR 매장 검증 성공 시 위치 앵커 갱신.
 * 좌표가 있으면 새 매장으로 앵커 갱신, 없으면 이전 앵커를 정리.
 *
 * @see #271 — 좌표 누락 시 이전 매장 앵커를 stale 상태로 두면 거리 정렬이
 *   엉뚱한 매장 기준으로 작동하므로, 새 QR 진입은 항상 이전 앵커 상태를
 *   덮어쓰는 방향으로 동작해야 함.
 */
function applyQrStoreAnchor(
  lat: number | null | undefined,
  lng: number | null | undefined,
  source: 'qr-pickup' | 'qr-table',
  storeId: number,
): void {
  if (typeof lat === 'number' && typeof lng === 'number') {
    setLocationAnchor({ lat, lng, source, storeId });
  } else {
    clearLocationAnchor();
  }
}

export function QRVerifyPage() {
  const { qrCode } = useParams<{ qrCode: string }>();
  const navigate = useNavigate();
  // #522: 15 locale 전수 — language(3종) 대신 locale 로 직접 조회
  const { locale } = useApp();
  const t = useMemo(() => getQrSessionTranslation(locale), [locale]);
  const tOccupied = useMemo(() => getQrOccupiedTranslation(locale), [locale]);
  const [state, setState] = useState<VerifyState>({ status: 'loading' });
  const [guestCount, setGuestCount] = useState(1);
  // #674 — 점유 화면 '다시 시도' 가 QR 재검증을 트리거하기 위한 nonce
  const [verifyNonce, setVerifyNonce] = useState(0);

  useEffect(() => {
    if (!qrCode) {
      setState({ status: 'error', message: t.noQrCode });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response: QrVerifyResponse = await verifyQrCode(qrCode);
        if (cancelled) return;

        if (response.data.type === 'pickup') {
          if (!response.data.store.pickup_available) {
            setState({ status: 'error', message: t.pickupNotSupported });
            return;
          }
          // #271: 새 QR 매장 = 새 위치 기준점.
          //       좌표가 있으면 앵커 갱신, 없으면 이전 앵커 정리(다른 매장 좌표가 stale해지지 않도록).
          //       BE 좌표 응답이 부분 배포되거나 일부 매장에 좌표 미등록인 상황에서
          //       이전 매장 좌표가 잘못된 거리 정렬 기준으로 재사용되는 것을 방지.
          applyQrStoreAnchor(
            response.data.store.latitude,
            response.data.store.longitude,
            'qr-pickup',
            response.data.store.id,
          );
          navigate(getRestaurantPath(String(response.data.store.id)), { replace: true });
          return;
        }

        if (response.data.type === 'view') {
          // view QR은 주문/위치와 무관 — applyQrStoreAnchor 갱신 대상 제외
          navigate(`${getRestaurantPath(String(response.data.store.id))}?mode=view`, { replace: true });
          return;
        }

        if (response.data.type === 'table') {
          const tableResponse = response as QrTableVerifyResponse;
          const { active_session: activeSession, table } = tableResponse.data;

          // #271: 동일 정책 — 좌표 있으면 갱신, 없으면 이전 앵커 정리
          applyQrStoreAnchor(
            table.store.latitude,
            table.store.longitude,
            'qr-table',
            table.store.id,
          );

          if (activeSession) {
            const hasBootstrapEntry =
              activeSession.split_payment_available &&
              typeof tableResponse.data.entry_token === 'string'
              && tableResponse.data.entry_token !== ''
              && typeof tableResponse.data.entry_expires_in === 'number'
              && Number.isFinite(tableResponse.data.entry_expires_in)
              && tableResponse.data.entry_expires_in > 0;
            const restoredToken = restoreActiveTableSessionFromStorage({
              activeSession,
              table,
              splitPaymentAvailable: activeSession.split_payment_available,
            });

            if (restoredToken) {
              navigate(
                getRestaurantTablePath(String(table.store.id), table.table_number),
                { replace: true }
              );
              return;
            }

            if (hasBootstrapEntry) {
              persistTableSessionEntry({
                entryToken: tableResponse.data.entry_token as string,
                entryExpiresIn: tableResponse.data.entry_expires_in as number,
                activeSession,
                table,
              });
              navigate(
                getRestaurantTablePath(String(table.store.id), table.table_number),
                { replace: true },
              );
              return;
            }

            // #674 — 복원 토큰이 없으면 다른 일행이 점유 중인 테이블.
            //        세션 시작은 서버가 어차피 409 로 차단하므로 (정책 보존, #1539 범위 외)
            //        도달 불가능한 시작 UI 대신 점유 안내 화면을 즉시 표시.
            setState({ status: 'occupied', session: activeSession, data: tableResponse });
            return;
          }

          setState({
            status: 'table',
            data: tableResponse,
          });
          return;
        }

        // 알 수 없는 type — 무한 로딩 방지
        logger.error('QR verification: unknown QR type:', (response as { data: { type: string } }).data.type);
        setState({ status: 'error', message: t.unknownQrType });
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiClientError && error.code === 'QR_DISABLED') {
          setState({ status: 'disabled', message: getDisabledQrMessage(locale) });
          return;
        }
        const message = error instanceof Error ? error.message : t.unknownError;
        logger.error('QR code verification failed:', error);
        setState({ status: 'error', message });
      }
    })();

    return () => { cancelled = true; };
  }, [qrCode, navigate, t, verifyNonce, locale]);

  /** #674 — 점유 화면 '다시 시도': QR 재검증 (점유 해제 후 재진입 동선) */
  const handleRetryVerify = useCallback(() => {
    setState({ status: 'loading' });
    setVerifyNonce((nonce) => nonce + 1);
  }, []);

  const handleStartSession = useCallback(async () => {
    if (!qrCode || state.status !== 'table') return;
    const tableState = state.data;
    setState({ status: 'starting-session', data: tableState });

    try {
      // 위치 권한은 서버가 게이트를 켠 매장의 빈 테이블에서만 요청한다.
      // 기존 매장은 브라우저 위치 팝업 없이 기존 QR 시작 동작을 유지한다.
      const coordinates = tableState.data.location_gate?.required
        ? await requestCurrentCoordinates()
        : null;
      const session = await startTableSession(qrCode, guestCount, coordinates);
      const tableData = session.data.table;
      persistTableSession({
        token: session.data.session_token,
        sessionId: session.data.id,
        expiresAt: session.data.expires_at,
        table: tableData,
        splitPaymentAvailable: session.data.split_payment_available,
      });
      navigate(
        getRestaurantTablePath(String(tableData.store.id), tableData.table_number),
        { replace: true }
      );
    } catch (error) {
      // #674 — fail-safe 3분기:
      //   1. 점유 응답 (error_code 또는 data.active_session) → 점유 전용 화면
      //   2. 그 외 구조화 message → 그 message 표시 (client.ts 가 i18n/한국어 차단 처리)
      //   3. message 없음/비정형 → 기존 generic fallback (무회귀)
      const occupiedSession = extractOccupiedTableSession(error);
      if (occupiedSession) {
        logger.warn('Table session start blocked: table occupied', occupiedSession.id);
        setState({ status: 'occupied', session: occupiedSession, data: tableState });
        return;
      }

      const proximityMessage = error instanceof ApiClientError
        ? getProximityErrorMessage(error.code, t)
        : null;
      const message = proximityMessage
        ?? (error instanceof Error ? error.message : t.sessionStartFailed);
      if (proximityMessage) {
        // GPS rejection is an expected customer-facing gate outcome, not an app/runtime error.
        // Keep it out of console-error diagnostics while retaining a local warning for support.
        logger.warn('Table session start blocked by proximity gate:', error);
      } else {
        logger.error('Table session start failed:', error);
      }
      setState({ status: 'error', message, data: tableState });
    }
  }, [qrCode, guestCount, navigate, state, t]);

  // --- 렌더링 ---

  if (state.status === 'loading' || state.status === 'starting-session') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-foreground text-lg">
          {state.status === 'loading' ? t.verifying : (t.gpsChecking ?? t.startingSession)}
        </p>
      </div>
    );
  }

  // #674 — 점유 테이블 전용 화면: 서버 active_session 요약 + 직원 문의 안내
  if (state.status === 'occupied') {
    const occupiedTable = state.data?.data.table ?? null;
    const startedAtText = formatStartedAt(state.session.started_at);
    const elapsedMinutes = resolveElapsedMinutes(state.session);
    const elapsedText =
      elapsedMinutes !== null
        ? tOccupied.elapsedTime(Math.floor(elapsedMinutes / 60), elapsedMinutes % 60)
        : null;

    return (
      <div
        className="flex flex-col items-center justify-center min-h-[70vh] gap-5 px-6"
        data-testid="qr-occupied"
      >
        <Users className="h-12 w-12 text-primary" />

        <div className="text-center">
          {occupiedTable && (
            <>
              <h2 className="text-2xl font-bold text-foreground">{occupiedTable.store.name}</h2>
              <p className="text-muted-foreground mt-1">
                {t.tableLabel(occupiedTable.display_name, occupiedTable.capacity)}
              </p>
            </>
          )}
          <p className="text-lg font-semibold text-foreground mt-3">{tOccupied.title}</p>
        </div>

        {(startedAtText || elapsedText) && (
          <div className="w-full max-w-xs rounded-lg border border-border bg-muted/40 px-4 py-3 flex flex-col gap-2">
            {startedAtText && (
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {tOccupied.startedAtLabel}
                </span>
                <span className="font-medium text-foreground tabular-nums">{startedAtText}</span>
              </div>
            )}
            {elapsedText && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{tOccupied.elapsedLabel}</span>
                <span className="font-medium text-foreground tabular-nums">{elapsedText}</span>
              </div>
            )}
          </div>
        )}

        <p className="text-muted-foreground text-sm text-center max-w-xs">
          {tOccupied.description}
        </p>

        <button
          onClick={handleRetryVerify}
          className="text-primary hover:underline"
        >
          {t.retry}
        </button>
        <button
          onClick={() => navigate('/', { replace: true })}
          className="flex items-center gap-2 text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.goHome}
        </button>
      </div>
    );
  }

  if (state.status === 'disabled' || state.status === 'error') {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[70vh] gap-4 px-6"
        data-testid={state.status === 'disabled' ? 'qr-disabled' : undefined}
      >
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-foreground text-lg text-center">{state.message}</p>
        {state.status === 'error' && state.data && (
          <button
            onClick={() => setState({ status: 'table', data: state.data! })}
            className="text-primary hover:underline mt-2"
          >
            {t.retry}
          </button>
        )}
        <button
          onClick={() => navigate('/', { replace: true })}
          className="flex items-center gap-2 text-primary hover:underline mt-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.goHome}
        </button>
      </div>
    );
  }

  // state.status === 'table'
  const { table } = state.data.data;

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] gap-6 px-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground">{table.store.name}</h2>
        <p className="text-muted-foreground mt-1">
          {t.tableLabel(table.display_name, table.capacity)}
        </p>
      </div>

      <div className="flex flex-col items-center gap-3 w-full max-w-xs">
        <label className="flex items-center gap-2 text-foreground">
          <Users className="h-5 w-5" />
          {t.guestCount}
        </label>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setGuestCount(prev => Math.max(1, prev - 1))}
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-lg hover:bg-muted"
          >
            -
          </button>
          <span className="text-2xl font-bold w-8 text-center">{guestCount}</span>
          <button
            onClick={() => setGuestCount(prev => Math.min(20, prev + 1))}
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-lg hover:bg-muted"
          >
            +
          </button>
        </div>
      </div>

      <button
        onClick={handleStartSession}
        className="w-full max-w-xs bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
      >
        {t.startOrder}
      </button>

      <button
        onClick={() => navigate('/', { replace: true })}
        className="text-muted-foreground text-sm hover:underline"
      >
        {t.cancel}
      </button>
    </div>
  );
}
